import * as React from "react";
import { cn } from "../../lib/utils";
import { Input } from "./input";

const isEmptyRaw = (raw) => raw === "" || raw === null || raw === undefined;

/**
 * "05" → "5", "007.5" → "7.5"; "0.5", "0", "0." and ".5" are left alone.
 *
 * Hour fields sit at 0 most of the time, so typing into one without selecting
 * first produced "05" — the field fought every estimator who typed a half
 * hour. Stripping the redundant zero as it is typed is what makes "0.5" and
 * ".5" both land naturally.
 */
const stripLeadingZeros = (raw) =>
  typeof raw === "string" ? raw.replace(/^(-?)0+(?=\d)/, "$1") : raw;

/**
 * NumberInput — thin wrapper over `<Input type="number">` for the estimate
 * form. Handles the concerns that every numeric field in the app shares:
 *
 *  - `align` prop controls text alignment (money = right, qty = center)
 *  - `inputMode="decimal"` for better mobile keyboards
 *  - Browser spinner arrows hidden (they look noisy in dense tables); users
 *    can still type or use step buttons via keyboard
 *  - Null / undefined values render as empty string (avoids `NaN` warnings)
 *  - `onValueChange(number | null)` gives parsed numbers — null for empty
 *  - While focused, the field may be transiently empty. Parent defaults
 *    (`value ?? 1`) must not snap the caret back mid-keystroke: select-all
 *    → Backspace → type `86` used to become `186`. `emptyBlurValue` commits
 *    that default only on blur if the field is still empty.
 *
 * The underlying `Input` primitive supplies borders, focus ring, and theme
 * colors from Tailwind tokens, so a NumberInput looks like every other
 * control in the app by construction.
 */
const NumberInput = React.forwardRef(
  (
    {
      className,
      align = "right",
      value,
      onValueChange,
      onChange,
      onFocus,
      onBlur,
      step = "0.01",
      min,
      max,
      placeholder,
      emptyBlurValue,
      ...props
    },
    ref,
  ) => {
    const alignClass =
      align === "center"
        ? "text-center"
        : align === "left"
          ? "text-left"
          : "text-right";

    const [isFocused, setIsFocused] = React.useState(false);
    const [draft, setDraft] = React.useState("");
    const defersEmpty = emptyBlurValue !== undefined && emptyBlurValue !== null;

    const emitParsed = (raw) => {
      if (!onValueChange) return;
      if (isEmptyRaw(raw)) {
        onValueChange(null);
        return;
      }
      const parsed = parseFloat(raw);
      onValueChange(Number.isNaN(parsed) ? null : parsed);
    };

    const handleFocus = (e) => {
      setIsFocused(true);
      const current =
        value === null || value === undefined || value === ""
          ? ""
          : String(value);
      setDraft(current);
      // A field showing a bare 0 is a placeholder, not a value someone means to
      // append to: select it so the first keystroke replaces it.
      if (current === "0" && e.target.select) e.target.select();
      if (onFocus) onFocus(e);
    };

    const handleChange = (e) => {
      const raw = stripLeadingZeros(e.target.value);
      setDraft(raw);
      if (onChange) onChange(e);
      if (isEmptyRaw(raw) && defersEmpty) {
        // Leave parent state alone until blur — a `?? 1` (or similar)
        // default would put the caret after that digit and the next
        // keystrokes would append.
        return;
      }
      emitParsed(raw);
    };

    const handleBlur = (e) => {
      setIsFocused(false);
      const raw = e.target.value;
      if (isEmptyRaw(raw) && defersEmpty) {
        if (onValueChange) onValueChange(emptyBlurValue);
      }
      if (onBlur) onBlur(e);
    };

    return (
      <Input
        ref={ref}
        type="number"
        inputMode="decimal"
        step={step}
        min={min}
        max={max}
        placeholder={placeholder}
        className={cn(
          alignClass,
          // Hide browser number-spinner arrows for a clean dense-table look
          "[appearance:textfield]",
          "[&::-webkit-outer-spin-button]:appearance-none",
          "[&::-webkit-outer-spin-button]:m-0",
          "[&::-webkit-inner-spin-button]:appearance-none",
          "[&::-webkit-inner-spin-button]:m-0",
          className,
        )}
        {...props}
        value={isFocused ? draft : (value ?? "")}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    );
  },
);
NumberInput.displayName = "NumberInput";

export { NumberInput };
